<template>
  <div class="p-4 max-w-lg mx-auto space-y-4">
    <h2 class="text-xl font-bold">Categorías</h2>

    <!-- FORMULARIO -->
    <div class="border rounded p-3 space-y-3">
      <input v-model="nombre" type="text" placeholder="Nombre de categoría" class="w-full p-2 border rounded" />
      <input v-model="icono" type="text" placeholder="Icono (emoji)" class="w-full p-2 border rounded" />
      <textarea v-model="descripcion" placeholder="Descripción corta" class="w-full p-2 border rounded"></textarea>

      <div class="flex items-center gap-2">
        <input type="file" accept="image/*" @change="onFileChange" />
        <img v-if="previewUrl" :src="previewUrl" class="w-20 h-20 object-cover rounded border" />
      </div>

      <div class="flex items-center gap-3">
        <label class="flex items-center gap-2">
          <input type="checkbox" v-model="visibleEnLanding" />
          <span>Mostrar en landing</span>
        </label>

        <label class="flex items-center gap-2">
          <input type="checkbox" v-model="visible" />
          <span>Visible</span>
        </label>
      </div>

      <!-- Switches simples ON/OFF -->
      <div class="flex items-center gap-3">
        <button
          type="button"
          @click="formMostrarWeb = !formMostrarWeb"
          class="px-3 py-1 border rounded"
        >
          Web: {{ formMostrarWeb ? 'ON' : 'OFF' }}
        </button>

        <button
          type="button"
          @click="formMostrarPOS = !formMostrarPOS"
          class="px-3 py-1 border rounded"
        >
          POS: {{ formMostrarPOS ? 'ON' : 'OFF' }}
        </button>
      </div>

      <label class="block text-sm text-gray-500">
        Padre (opcional)
        <select v-model="parentId" class="w-full border p-2 rounded">
          <option :value="null">Ninguna (principal)</option>
          <option v-for="c in categorias" :key="c.id" :value="c.id">{{ c.nombre }}</option>
        </select>
      </label>

      <div class="flex gap-2">
        <button @click="guardarCategoria" class="flex-1 bg-blue-600 text-white py-2 rounded">
          {{ idCategoriaEditando ? 'Actualizar' : 'Crear' }}
        </button>
        <button v-if="idCategoriaEditando" @click="resetForm" class="flex-1 border py-2 rounded">
          Cancelar
        </button>
      </div>
    </div>

    <!-- LISTA -->
    <div v-if="categorias.length" class="space-y-2">
      <div
        v-for="c in categorias"
        :key="c.id"
        class="border rounded p-3 flex items-start justify-between gap-3"
      >
        <div class="space-y-1">
          <div class="font-semibold flex items-center gap-2">
            <span v-if="c.icono">{{ c.icono }}</span>
            <span>{{ c.nombre }}</span>
          </div>
          <div class="text-sm text-gray-600">{{ c.descripcion }}</div>
          <div v-if="c.parentId" class="text-xs text-gray-500">
            ↪ Subcategoría de: {{ getCategoriaNombre(c.parentId) }}
          </div>
          <div class="text-xs text-gray-500">
            Estado: {{ c.visible ? 'Visible' : 'Oculta' }} | Web: {{ c.mostrarWeb ? 'ON' : 'OFF' }} | POS: {{ c.mostrarPOS ? 'ON' : 'OFF' }}
          </div>
        </div>

        <div class="flex flex-col gap-2 items-end">
          <div class="flex gap-2">
            <button @click="toggleSwitch(c, 'web')" class="px-3 py-1 border rounded">Web: {{ c.mostrarWeb ? 'ON' : 'OFF' }}</button>
            <button @click="toggleSwitch(c, 'pos')" class="px-3 py-1 border rounded">POS: {{ c.mostrarPOS ? 'ON' : 'OFF' }}</button>
            <button @click="toggleCampo(c.id, 'visible', !c.visible)" class="px-3 py-1 border rounded">
              {{ c.visible ? 'Ocultar' : 'Mostrar' }}
            </button>
          </div>
          <div class="flex gap-2 text-sm">
            <button @click="editarCategoria(c)" class="underline">Editar</button>
            <button @click="eliminarCategoria(c.id)" class="underline text-red-600">Borrar</button>
          </div>
        </div>
      </div>
    </div>

    <p v-else class="text-gray-500">No hay categorías.</p>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { db, storage } from '@/firebase'
import { collection, getDocs, deleteDoc, doc, addDoc, updateDoc } from 'firebase/firestore'

import { ref as sRef, uploadBytes, getDownloadURL } from 'firebase/storage'

/** -------------------------
 *  Helpers consistencia
 *  ------------------------- */
function flagsDesdeMostrarEn(mostrar) {
  const opt = (mostrar || 'ambos').toLowerCase()
  return {
    mostrarPOS: opt === 'pos' || opt === 'ambos',
    mostrarWeb: opt === 'ecommerce' || opt === 'ambos'
  }
}
function mostrarEnDesdeFlags({ mostrarPOS, mostrarWeb }) {
  if (mostrarPOS && mostrarWeb) return 'ambos'
  if (mostrarPOS && !mostrarWeb) return 'pos'
  if (!mostrarPOS && mostrarWeb) return 'ecommerce'
  return 'pos' // política cuando ambos OFF
}

/** -------------------------
 *  Estado formulario
 *  ------------------------- */
const nombre = ref('')
const icono = ref('')
const descripcion = ref('')
const visibleEnLanding = ref(false)
const visible = ref(true)
const parentId = ref(null)
const file = ref(null)
const previewUrl = ref(null)
const formMostrarWeb = ref(true) // por defecto visibles
const formMostrarPOS = ref(true) // por defecto visibles
const idCategoriaEditando = ref(null)

/** -------------------------
 *  Listado
 *  ------------------------- */
const categorias = ref([])

const resetForm = () => {
  nombre.value = ''
  icono.value = ''
  descripcion.value = ''
  visibleEnLanding.value = false
  visible.value = true
  parentId.value = null
  file.value = null
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value)
  previewUrl.value = null
  formMostrarWeb.value = true
  formMostrarPOS.value = true
  idCategoriaEditando.value = null
}

const onFileChange = (e) => {
  file.value = e.target.files?.[0] || null
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value)
  previewUrl.value = file.value ? URL.createObjectURL(file.value) : null
}

/** -------------------------
 *  CRUD
 *  ------------------------- */
const guardarCategoria = async () => {
  if (!nombre.value) return alert('Nombre requerido')

  // Subir imagen si hay
  let imagenUrl = ''
  if (file.value) {
    // usamos timestamp para evitar choques de nombre
    const path = `categorias/${Date.now()}-${file.value.name}`
    const imgRef = sRef(storage, path)
    await uploadBytes(imgRef, file.value, { contentType: file.value.type, cacheControl: 'public, max-age=300' })
    imagenUrl = await getDownloadURL(imgRef)
  }

  // Derivar enum coherente
  const mostrarEn = mostrarEnDesdeFlags({ mostrarPOS: formMostrarPOS.value, mostrarWeb: formMostrarWeb.value })

  const data = {
    nombre: nombre.value,
    icono: icono.value || '',
    descripcion: descripcion.value || '',
    imagenUrl: imagenUrl || '',
    visibleEnLanding: !!visibleEnLanding.value,
    parentId: parentId.value || null,
    // visibilidad
    visible: !!visible.value,
    mostrarWeb: !!formMostrarWeb.value,
    mostrarPOS: !!formMostrarPOS.value,
    mostrarEn
  }

  if (idCategoriaEditando.value) {
    await updateDoc(doc(db, 'categorias', idCategoriaEditando.value), data)
    alert('Categoría actualizada')
  } else {
    // si quieres path de imagen estable por id, podrías setDoc con id pre-creado
    await addDoc(collection(db, 'categorias'), data)
    alert('Categoría creada')
  }

  resetForm()
  await cargarCategorias()
}

const cargarCategorias = async () => {
  const snap = await getDocs(collection(db, 'categorias'))
  categorias.value = snap.docs.map(d => {
    const data = d.data()
    // Normalizar datos viejos:
    // - si hay mostrarEn => derivar flags
    // - si no hay => usar booleans existentes con defaults true
    const fromEnum = typeof data.mostrarEn === 'string' && data.mostrarEn.length > 0
    const flags = fromEnum
      ? flagsDesdeMostrarEn(data.mostrarEn)
      : { mostrarPOS: data.mostrarPOS ?? true, mostrarWeb: data.mostrarWeb ?? true }

    return {
      id: d.id,
      ...data,
      mostrarPOS: !!flags.mostrarPOS,
      mostrarWeb: !!flags.mostrarWeb,
      visible: data.visible ?? true
    }
  })
}

const eliminarCategoria = async (id) => {
  if (!confirm('¿Eliminar esta categoría?')) return
  await deleteDoc(doc(db, 'categorias', id))
  await cargarCategorias()
}

const editarCategoria = (c) => {
  idCategoriaEditando.value = c.id
  nombre.value = c.nombre
  icono.value = c.icono || ''
  descripcion.value = c.descripcion || ''
  visibleEnLanding.value = !!c.visibleEnLanding
  visible.value = c.visible ?? true
  parentId.value = c.parentId || null
  formMostrarWeb.value = c.mostrarWeb ?? true
  formMostrarPOS.value = c.mostrarPOS ?? true
  if (previewUrl.value) URL.revokeObjectURL(previewUrl.value)
  previewUrl.value = c.imagenUrl || null
}

const getCategoriaNombre = (id) => {
  const cat = categorias.value.find(x => x.id === id)
  return cat ? cat.nombre : '—'
}

/** -------------------------
 *  Toggles de la lista
 *  ------------------------- */
const toggleCampo = async (id, campo, valor) => {
  await updateDoc(doc(db, 'categorias', id), { [campo]: valor })
  await cargarCategorias()
}

// Alterna Web o POS y mantiene mostrarEn coherente
const toggleSwitch = async (cat, target) => {
  const next = {
    mostrarWeb: cat.mostrarWeb,
    mostrarPOS: cat.mostrarPOS
  }
  if (target === 'web') next.mostrarWeb = !next.mostrarWeb
  if (target === 'pos') next.mostrarPOS = !next.mostrarPOS

  const mostrarEn = mostrarEnDesdeFlags(next)
  await updateDoc(doc(db, 'categorias', cat.id), {
    mostrarWeb: next.mostrarWeb,
    mostrarPOS: next.mostrarPOS,
    mostrarEn
  })
  await cargarCategorias()
}

onMounted(cargarCategorias)
</script>
